import type { Facets, FetchFacetsParams } from '@iheartjane/dm-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSDK } from '@jane/shared-ecomm/providers';

import type { UseFetchPlacementProps } from './useFetchPlacement';

type UseFacetsProps = Omit<
  UseFetchPlacementProps<FetchFacetsParams>,
  'dependencies'
>;

const DEFAULT_SEARCH_FACETS = [
  'activities',
  'aggregate_rating',
  'applicable_bulk_special_ids',
  'applicable_bundle_special_ids.discount',
  'applicable_bundle_special_ids.qualify',
  'applicable_cart_total_special_ids',
  'applicable_special_ids',
  'available_weights',
  'brand_subtype',
  'brand',
  'bucket_price',
  'category',
  'custom_menu_row_ids',
  'feelings',
  'has_brand_discount',
  'kind',
  'percent_cbd',
  'percent_thc',
  'root_types',
  'compound_names',
];

export const useFacets = (props: UseFacetsProps) => {
  const {
    storeId,
    searchFacets = DEFAULT_SEARCH_FACETS,
    searchFilter,
    searchQuery,
    enabled = true,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [facets, setFacets] = useState<Facets | undefined>(undefined);
  const { sdk } = useSDK();

  const fetchFacets = useCallback(async () => {
    if (!sdk) {
      return;
    }
    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);
    setFacets(undefined);

    try {
      const facetResponse = enabled
        ? await sdk.fetchFacets({
            storeId,
            searchFacets,
            searchFilter,
            searchQuery,
          })
        : undefined;
      setFacets(facetResponse);
      setIsSuccess(true);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, searchFilter, searchQuery, storeId, sdk]);

  useEffect(() => {
    fetchFacets();
  }, [fetchFacets]);

  const response = useMemo(
    () => ({
      facets,
      isError,
      isLoading,
      isSuccess,
    }),
    [facets, isError, isLoading, isSuccess]
  );

  return response;
};
